<template>
  <div class="cf-container-full-width cf-flex cf-flex--justify-center">
    <div class="cf-flex cf-flex--column cf-flex--gap-16 cf-max-width-600">
      <div
        ref="carousel"
        class="cf-carousel__items"
      >
        <template v-if="paymentMethods.length > 0">
          <cf-payment-method-to-scan
            v-for="(paymentMethod, idx) in paymentMethods"
            :key="paymentMethod.cardId || paymentMethod.creditCardId"
            :component-idx="idx"
            :focused-payment-method-idx="focusedCard"
            :payment-method="paymentMethod"
            :payment-methods="paymentMethods"
            :tip-amount="tipAmount"
            :offer-redemption-code="offerRedemptionCode"
            :offer-key-token="offerKeyToken"
          />
        </template>

        <div class="cf-carousel__item">
          <cf-add-payment-method />
        </div>
      </div>

      <!-- Page Dots -->
      <div
        v-if="numberOfDots > 1"
        class="cf-carousel__dots"
      >
        <div
          v-for="(_, idx) in numberOfDots"
          :key="idx"
          :class="['cf-carousel__dot', { 'cf-carousel__dot--active': idx === focusedCard }]"
        />
      </div>

      <!-- Tip Amount -->
      <transition name="fade-down-only-in">
        <div
          v-if="paymentMethods.length > 0 && !isScrolledToAddPaymentMethodCard"
          class="cf-card cf-flex cf-flex--column cf-flex--gap-16"
        >
          <cf-tip-selection
            v-model="tipAmount"
            label="Please select a tip"
            :total="tipAmount"
          />

          <cf-verify-account-cta />

          <!-- Rewards / Offers -->
          <cf-loading-wrapper
            v-if="singleScanOfferEnabled"
            :is-loading="isFetchingRedemptionCode"
            :loader-type="'section'"
          >
            <cf-offers-card-list
              :applied-offer="appliedOffer"
              :offers="offers.allOffers"
              @apply-offer="applyOffer"
              @remove-offer="removeOffer"
            />
          </cf-loading-wrapper>
        </div>
      </transition>
    </div>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import http from '@/services/http';

  import errorHandlers from '@/services/error-handlers';

  import CfPaymentMethodToScan from '../cf-payment-method-to-scan/index.vue';

  import CfOffersCardList from '../../cf-offers-card-list/index.vue';

  import CfAddPaymentMethod from '@/view/components/globals/cf-add-payment-method/index.vue';
  import CfTipSelection from '@/view/components/globals/cf-tip-selection/index.vue';
  import CfLoadingWrapper from '@/view/components/globals/cf-loading-wrapper/index.vue';
  import CfVerifyAccountCta from '@/view/components/globals/cf-verify-account-cta/index.vue';


  export default {
    name: 'ScanToPayTab',

    components: {
      CfPaymentMethodToScan,
      CfOffersCardList,
      CfAddPaymentMethod,
      CfTipSelection,
      CfLoadingWrapper,
      CfVerifyAccountCta
    },

    metaInfo() {
      return {
        title: 'Credit Cards'
      };
    },

    data() {
      return {
        isLoading: false,
        focusedCard: 0,
        tipAmount: 0,
        appliedOffer: {},
        isFetchingRedemptionCode: false,
        offerRedemptionCode: null,
        offerKeyToken: null,
        realTimeOrders: null
      };
    },

    computed: {
      ...mapGetters('offers', ['loyalty', 'offers']),
      ...mapGetters('session', ['currentUser']),
      ...mapGetters('locations', ['timeZone']),
      ...mapGetters('merchant', ['merchant', 'shouldUseOfferKeyToken', 'shouldGenerateRedemptionCode']),

      ...mapGetters('savedCards', ['savedCards']),
      ...mapGetters('giftCards', ['giftCards']),

      paymentMethods() {
        return [
          ...this.giftCards.savedCards,
          ...this.savedCards.cards
        ];
      },

      singleScanOfferEnabled() {
        return this.merchant.features['single-scan-enable-activate-offer-option'];
      },

      hasOffers() {
        return !!this.offers.allOffers.length;
      },

      offerThresholds() {
        return this.loyalty.unlockedThresholds;
      },

      numberOfDots() {
        /** Need to add 1 to account for final "add method" card */
        return this.paymentMethods.length + 1;
      },

      isScrolledToAddPaymentMethodCard() {
        return this.focusedCard === this.paymentMethods.length;
      }
    },

    watch: {
      'giftCards.cards': 'giftCardsChangeWatcher',
      'savedCards.cards': 'creditCardsChangeWatcher',

      realTimeOrders(newOrders, oldOrders) {
        /**
         * The `oldOrders` value will start as `null` before receiving the initial state of
         * the user's orders from the real-time database. When checking for new orders, we
         * must ensure that the `oldOrders` value has been set with the initial orders
         * before comparing the amount of new vs old orders.
         */
        const hasNewOrder = !!oldOrders && oldOrders.length < newOrders.length;

        if (hasNewOrder) {
          const latestOrder = newOrders[newOrders.length - 1];

          this.$openModal(
            'scanToPayReceiptModal',
            {
              receiptToken: latestOrder.ReceiptToken,
              confirmHandler: this.refreshData
            },
            { isRequired: true }
          );
        }
      }
    },

    mounted() {
      this.mountedHandler();
    },

    beforeUnmount() {
      this.beforeDestroyHandler();
    },


    methods: {
      async mountedHandler() {
        this.$refs.carousel.addEventListener('scroll', this.scrollHandler);

        if (this.$realTimeOrdersDatabase.isConfigurationAvailable()) {
          await this.$realTimeOrdersDatabase.connect(this.observeRealTimeDb.bind(this));
        }

        if (this.$route.query?.offerAllocationPublicId) {
          this.handleRouteOffer();
        }
        else if (this.$route.query?.thresholdPublicId) {
          this.handleRouteThreshold();
        }
      },

      beforeDestroyHandler() {
        this.$refs.carousel.removeEventListener('scroll', this.scrollHandler);

        if (this.$realTimeOrdersDatabase.isConfigurationAvailable()) {
          this.$realTimeOrdersDatabase.disconnect();
        }
      },

      handleRouteOffer() {
        const { offerAllocationPublicId } = this.$route.query;

        const selectedOffer = this.offers.allOffers.find(offer => offer.offerAllocationPublicId === offerAllocationPublicId);

        this.applyOffer(selectedOffer);
      },

      handleRouteThreshold() {
        const { thresholdPublicId } = this.$route.query;
        const selectedThreshold = this.offerThresholds.find(threshold => threshold.publicId === thresholdPublicId);

        // We need to track the offerKeyToken and consversion threshold publicId while keeping
        // an offer-like object so the existing applyOffer code continues to work
        this.applyOffer({
          ...selectedThreshold.rewardOffer,
          offerKeyToken: selectedThreshold.offerKeyToken,
          thresholdPublicId
        });
      },

      scrollHandler() {
        // Careful when altering math logic as it determines which dot is focused and drives the items qr code rendering.
        const numberOfChildItems = this.$refs.carousel.children.length;
        const childItemWidth = this.$refs.carousel.children[0].clientWidth;
        const withOfChildrenContainer = numberOfChildItems * childItemWidth;
        const scrolled = this.$refs.carousel.scrollLeft;
        const result = (scrolled / withOfChildrenContainer) * numberOfChildItems;

        if (Number.isInteger(result)) {
          this.focusedCard = result;
        }
      },

      async applyOffer(offer) {
        this.isFetchingRedemptionCode = true;

        try {
          // For the new offer key token flow, we don't need to generate redemption codes
          // Reward Store thresholds are always offer key token redemptions, whatever the My Rewards flow is
          if (this.shouldUseOfferKeyToken || offer.thresholdPublicId) {
            // The offer should already contain offerKeyToken so just set it, no API call needed
            this.offerRedemptionCode = null;
            this.offerKeyToken = offer.offerKeyToken;
          }
          else if (this.shouldGenerateRedemptionCode) {
            // Legacy flow - generate redemption code
            const payload = {
              ianaTimezoneId: this.timeZone,
              offerAllocationPublicId: offer.offerAllocationPublicId
            };

            const { data } = await http.post('/cardfree-api/v1/offer/me/redemption-codes', payload);

            this.offerRedemptionCode = data.redemptionCode;
            this.offerKeyToken = null;
          }
          this.appliedOffer = offer;

          let routerPayload;
          if (offer.offerAllocationPublicId) {
            routerPayload = { offerAllocationPublicId: offer.offerAllocationPublicId };
          }
          else if (offer.thresholdPublicId) {
            routerPayload = { thresholdPublicId: offer.thresholdPublicId };
          }

          if (JSON.stringify(this.$route.query) !== JSON.stringify(routerPayload)) {
            this.$router.replace({
              query: { ...routerPayload }
            });
          }
        }
        catch (error) {
          errorHandlers.generic(error);
        }
        finally {
          this.isFetchingRedemptionCode = false;
        }
      },

      async refreshData() {
        try {
          this.removeOffer();

          await this.$store.dispatch('giftCards/getSavedCards', { allowCache: false });

          await this.$store.dispatch('offers/getOffers');
        }
        catch (error) {
          errorHandlers.silent(error);
        }
      },

      removeOffer() {
        this.appliedOffer = {};

        this.offerRedemptionCode = '';
        this.offerKeyToken = null;
        this.$router.replace({ query: {} });
      },

      observeRealTimeDb(observableData) {
        this.realTimeOrders = observableData ? Object.values(observableData) : [];
      },

      giftCardsChangeWatcher(newCards, oldCards) {
        const hasNewCard = newCards.length > oldCards.length;

        if (hasNewCard) {
          this.$nextTick(() => {
            const scrollToIndex = !oldCards.length
              ? 0 /* scroll to first gift card if no old cards */
              : newCards.findIndex(newCard => !oldCards.some(oldCard => oldCard.cardId === newCard.cardId));

            this.$refs.carousel.children[scrollToIndex].scrollIntoView({ behavior: 'smooth' });
          });
        }
      },

      creditCardsChangeWatcher(newCards, oldCards) {
        const hasNewCard = newCards.length > oldCards.length;

        if (hasNewCard) {
          this.$nextTick(() => {
            const newCardIdx = !oldCards.length
              ? 0 /* scroll to first credit card if no old cards */
              : newCards.findIndex(newCard => !oldCards.some(oldCard => oldCard.creditCardId === newCard.creditCardId));

            /**
             * add the length of the gift cards to the new credit card index
             * to get the correct index in the carousel of gift & credit cards
             */
            const scrollToIndex = newCardIdx + this.giftCards.cards.length;

            this.$refs.carousel.children[scrollToIndex].scrollIntoView({ behavior: 'smooth' });
          });
        }
      }
    }
  };
</script>
